<template>
  <v-card
    ref="containerRef"
    class="create-post-card rounded-xl border bg-black-05"
    :class="{ 'dragging-active': isDragging }"
    style="border-color: rgb(var(--v-theme-primary)) !important; border-width: 2px !important; position: relative;"
    @dragover="handleDragOver"
    @dragenter="handleDragEnter"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <!-- Mention Suggestions Dropdown -->
    <MentionSuggestions
      v-if="mentionState.visible"
      :query="mentionState.query"
      :position="{ top: mentionState.top, left: mentionState.left }"
      @select="insertMention"
      @close="mentionState.visible = false"
    />

    <v-card-title v-if="title" class="px-4 pt-4 pb-0 text-h6 font-weight-black d-flex align-center">
      <v-icon icon="mdi-share-variant" class="mr-2" color="primary"></v-icon>
      {{ title }}
    </v-card-title>

    <div class="pa-4">
      <div class="d-flex align-start">
        <v-avatar size="48" color="surface-variant" style="flex-shrink: 0; margin-right: 14px; margin-top: 4px;">
          <v-img v-if="userProfile?.avatar" :src="userProfile.avatar"></v-img>
          <v-icon v-else icon="mdi-account-circle" size="32"></v-icon>
        </v-avatar>

        <div class="flex-grow-1">
          <v-textarea
            ref="textareaRef"
            v-model="text"
            :placeholder="placeholderText"
            variant="plain"
            auto-grow
            rows="2"
            max-rows="12"
            hide-details
            class="create-post-textarea"
            @focus="isExpanded = true"
            @input="handleMentionInput"
            @paste="handlePaste"
            :disabled="loading"
          ></v-textarea>

          <!-- Media Preview -->
          <div v-if="mediaPreviews.length > 0" class="media-preview-container mt-4 d-flex flex-wrap gap-2">
            <div v-for="(preview, index) in mediaPreviews" :key="index" class="preview-item position-relative">
              <v-img
                :src="preview.url"
                width="120"
                height="120"
                cover
                class="rounded-lg border border-white-05"
              ></v-img>
              <v-btn
                icon="mdi-close"
                size="x-small"
                color="black"
                class="remove-btn"
                @click="removeMedia(index)"
                :disabled="loading"
              ></v-btn>
              <div v-if="preview.type === 'video'" class="video-badge">
                <v-icon icon="mdi-video" size="x-small"></v-icon>
              </div>
            </div>
          </div>

          <!-- Quoted Post Preview (for Quotes) -->
          <div v-if="quote" class="mt-2 mb-2">
            <div class="d-flex align-center justify-space-between mb-1">
              <span class="text-caption text-grey">Quoting:</span>
              <v-btn icon="mdi-close" size="x-small" variant="text" @click="$emit('remove-quote')"></v-btn>
            </div>
            <QuotedPost :record="{ $type: 'app.bsky.embed.record#viewRecord', ...quote }" />
          </div>

          <!-- Instagram Preview -->
          <div v-if="instagramLinks.length > 0" class="mt-4 mb-2">
            <div class="text-caption text-grey mb-1">Instagram Preview:</div>
            <InstagramEmbed
              v-for="url in instagramLinks"
              :key="url"
              :url="url"
            />
          </div>

          <v-divider v-if="isExpanded || quote" class="my-4 border-white-05"></v-divider>

          <!-- Tag Selection Menu -->
          <div v-if="isExpanded" class="mb-4">
            <v-menu
              open-on-hover
              :close-on-content-click="false"
              location="bottom"
              offset="8"
              :transition="false"
              :width="containerRef?.$el?.offsetWidth"
              scrim="rgba(0,0,0,0.4)"
              overlay-class="tag-menu-overlay"
            >
              <template v-slot:activator="{ props: menuProps }">
                <v-btn
                  v-bind="menuProps"
                  variant="tonal"
                  color="primary"
                  rounded="pill"
                  size="small"
                  class="tag-activator-btn text-none font-weight-black"
                >
                  <v-icon icon="mdi-pound" start size="small"></v-icon>
                  <span v-if="selectedTags.length === 0" class="text-grey">Select tags...</span>
                  <div v-else class="selected-tags-display d-flex align-center flex-wrap ga-2 pl-2">
                    <span v-for="tagId in selectedTags" :key="tagId" class="tag-badge">#{{ tagId }}</span>
                  </div>
                </v-btn>
              </template>

              <v-card class="tag-menu-card pa-4 rounded-xl border border-white-05 bg-surface-dark shadow-2xl">
                <div class="text-caption font-weight-bold text-grey mb-3 px-1">SELECT CATEGORIES</div>
                <div class="tag-grid">
                  <v-chip
                    v-for="tag in TAG_CATEGORIES"
                    :key="tag.id"
                    :prepend-icon="tag.icon"
                    :color="selectedTags.includes(tag.id) ? 'primary' : 'white'"
                    :variant="selectedTags.includes(tag.id) ? 'flat' : 'outlined'"
                    size="small"
                    class="tag-pill font-weight-bold"
                    @click="toggleTag(tag.id)"
                    @mouseenter="hoveredTag = tag"
                    @mouseleave="hoveredTag = null"
                    :disabled="loading"
                  >
                    #{{ tag.id }}
                  </v-chip>
                </div>

                <!-- Tag Description Footer -->
                <v-divider class="my-3 border-white-05"></v-divider>
                <div class="tag-description-area min-height-40">
                  <div v-if="hoveredTag" class="animate-fade-in">
                    <div class="text-caption font-weight-black text-primary mb-1">
                      #{{ hoveredTag.id }} • {{ hoveredTag.label }}
                    </div>
                    <div class="text-caption text-grey-lighten-1 leading-tight">
                      {{ hoveredTag.description }}
                    </div>
                  </div>
                  <div v-else class="text-caption text-grey-darken-1 italic">
                    Arahkan kursor ke tag untuk melihat penjelasan...
                  </div>
                </div>
              </v-card>
            </v-menu>
          </div>

          <div v-if="isExpanded" class="d-flex align-center justify-space-between flex-wrap gap-2">
            <div class="d-flex align-center">
              <input
                type="file"
                ref="fileInput"
                class="d-none"
                accept="image/*,video/*"
                multiple
                @change="onFileSelected"
              />
              <v-btn
                icon="mdi-image-outline"
                variant="text"
                color="primary"
                size="small"
                @click="fileInput?.click()"
                :disabled="loading || mediaPreviews.length >= 4"
                title="Add Images/Video"
              ></v-btn>
              <v-menu :close-on-content-click="false" location="bottom start">
                <template v-slot:activator="{ props: emojiProps }">
                  <v-btn
                    v-bind="emojiProps"
                    icon="mdi-emoticon-outline"
                    variant="text"
                    color="primary"
                    size="small"
                    :disabled="loading"
                    title="Add Emoji"
                  ></v-btn>
                </template>
                <v-card class="rounded-lg shadow-xl border border-white-05 bg-surface-light" width="auto">
                  <Picker
                    :data="emojiIndexRef"
                    set="native"
                    :native="true"
                    @select="addEmoji"
                    :showPreview="true"
                    :showSkinTones="false"
                    :emojiTooltip="true"
                    theme="dark"
                  />
                </v-card>
              </v-menu>

              <!-- Interaction Settings -->
              <v-btn
                variant="text"
                color="primary"
                size="small"
                class="text-none"
                :disabled="loading"
                @click="showInteractionDialog = true"
              >
                <v-icon :icon="interactionIcon" start></v-icon>
                {{ interactionLabel }}
                <v-icon v-if="!interactionSettings.allowQuotes" icon="mdi-cancel" size="x-small" color="error" class="ml-1"></v-icon>
              </v-btn>

              <InteractionSettingsDialog
                v-model="showInteractionDialog"
                :settings="interactionSettings"
                @save="onInteractionSettingsSave"
                @close="showInteractionDialog = false"
              />
            </div>

            <div class="d-flex align-center" style="gap: 10px;">
              <template v-if="rawTextCount > 0">
                <v-progress-circular
                  :model-value="(textCount / 300) * 100"
                  size="24"
                  width="2"
                  :color="textCount > 280 ? 'error' : 'primary'"
                ></v-progress-circular>
                <span
                  v-if="textCount > 240"
                  class="text-caption font-weight-bold"
                  :style="{ color: textCount > 300 ? 'rgb(var(--v-theme-error))' : textCount > 280 ? '#f59e0b' : 'rgba(255,255,255,0.5)' }"
                >{{ 300 - textCount }}</span>
              </template>

              <!-- GIF processing status -->
              <div v-if="gifStatus" class="d-flex align-center" style="gap: 8px;">
                <v-progress-circular
                  :model-value="gifStatus.percent"
                  size="22"
                  width="2"
                  color="primary"
                  :indeterminate="gifStatus.percent === 0"
                ></v-progress-circular>
                <span class="text-caption text-grey-lighten-1">
                  {{ gifStatus.label }}<template v-if="gifStatus.percent > 0"> · {{ gifStatus.percent }}%</template>
                </span>
              </div>

              <v-btn
                :color="textCount > 300 ? 'secondary' : 'primary'"
                variant="flat"
                class="rounded-pill font-weight-black px-6"
                :loading="loading && !gifStatus"
                :disabled="!isValid || loading"
                @click="textCount > 300 ? $emit('convert-to-note', text) : handlePost()"
              >
                <v-icon v-if="textCount > 300" icon="mdi-arrow-right" start></v-icon>
                {{ textCount > 300 ? 'Make as Note' : 'Post' }}
              </v-btn>
            </div>
          </div>

          <!-- Footnote -->
          <div v-if="footnote" class="mt-4 pa-3 rounded-lg border border-primary-05 bg-primary-05 d-flex align-center">
            <v-icon icon="mdi-information-outline" size="small" color="primary" class="mr-3"></v-icon>
            <span class="text-caption text-grey-lighten-1 footnote-text">
              {{ footnote }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, markRaw, shallowRef, watch } from 'vue';
import { userProfile, createPost, uploadBlob, uploadVideo, showAlert } from '@/services/bluesky';
import data from "emoji-mart-vue-fast/data/all.json";
import "emoji-mart-vue-fast/css/emoji-mart.css";
import { Picker, EmojiIndex } from "emoji-mart-vue-fast/src";
import QuotedPost from './QuotedPost.vue';
import InteractionSettingsDialog from './InteractionSettingsDialog.vue';
import InstagramEmbed, { extractInstagramLinks } from './InstagramEmbed.vue';
import MentionSuggestions from './MentionSuggestions.vue';
import { compressImage, isGifTooLarge } from '@/utils/image';

// Fix for Vue 3 warning crash by safely overriding prototype toString
if (EmojiIndex && EmojiIndex.prototype) {
  try {
    EmojiIndex.prototype.toString = function() { return '[EmojiIndex]'; };
    EmojiIndex.prototype.valueOf = function() { return '[EmojiIndex]'; };
    // @ts-ignore - Ignore index signature error for missing Symbol.toPrimitive type definition
    EmojiIndex.prototype[Symbol.toPrimitive] = function(hint: string) { return '[EmojiIndex]'; };
  } catch (e) {
    console.warn('Failed to polyfill EmojiIndex primitive conversions', e);
  }
}

const props = defineProps({
  title: { type: String, default: '' },
  footnote: { type: String, default: '' },
  initialText: { type: String, default: '' },
  initialTags: { type: Array as () => string[], default: () => ['gamedevid'] },
  quote: { type: Object, default: null }
});

const emit = defineEmits(['success', 'convert-to-note', 'remove-quote', 'close']);

const emojiIndex = markRaw(new EmojiIndex(data));
const emojiIndexRef = shallowRef(emojiIndex);
const text = ref(props.initialText);
const isExpanded = ref(false);
const isDragging = ref(false);
const containerRef = ref<any>(null);
const selectedTags = ref<string[]>([...props.initialTags]);
const hoveredTag = ref<any>(null);

watch(() => props.initialTags, (newTags) => {
  selectedTags.value = [...newTags];
});
const textareaRef = ref<any>(null);

const mentionState = ref({
  visible: false,
  query: '',
  top: 0,
  left: 0,
  cursorPos: 0
});

const handleMentionInput = (e: any) => {
  const el = e.target;
  const value = el.value;
  const cursorPos = el.selectionStart;
  
  // Look back from cursor for '@' pattern: space or start of line followed by @ and handle chars
  const textBefore = value.substring(0, cursorPos);
  const lastAtMatch = textBefore.match(/(?:^|\s)@([a-zA-Z0-9.-]*)$/);
  
  if (lastAtMatch) {
    const rect = el.getBoundingClientRect();
    mentionState.value = {
      visible: true,
      query: lastAtMatch[1],
      top: rect.top + 40,
      left: rect.left + 20,
      cursorPos: cursorPos
    };
  } else {
    mentionState.value.visible = false;
  }
};

const insertMention = (user: any) => {
  const textBefore = text.value.substring(0, mentionState.value.cursorPos);
  const lastAtPos = textBefore.lastIndexOf('@');
  const beforeAt = textBefore.substring(0, lastAtPos);
  const textAfter = text.value.substring(mentionState.value.cursorPos);
  
  text.value = beforeAt + `@${user.handle} ` + textAfter;
  mentionState.value.visible = false;
  
  // Focus back to the textarea
  setTimeout(() => {
    if (textareaRef.value) {
      const textarea = textareaRef.value.$el.querySelector('textarea');
      if (textarea) {
        textarea.focus();
        const newPos = beforeAt.length + user.handle.length + 2; 
        textarea.selectionStart = textarea.selectionEnd = newPos;
      }
    }
  }, 50);
};

watch(() => props.initialText, (newVal) => {
  text.value = newVal;
  if (newVal.length > 0) {
    isExpanded.value = true;
  }
}, { immediate: true });

watch(() => props.initialTags, (newTags) => {
  if (newTags && newTags.length > 0) {
    selectedTags.value = [...newTags];
  }
}, { immediate: true });

watch(() => props.quote, (newVal) => {
  if (newVal) isExpanded.value = true;
}, { immediate: true });
const loading = ref(false);
const gifStatus = ref<{ label: string; percent: number } | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const placeholderText = 'What are you working on?';
const textFiles = ref<File[]>([]);
const mediaFiles = ref<File[]>([]);
const mediaPreviews = ref<any[]>([]);

import type { InteractionSettings } from '@/services/bluesky';

// Interaction Settings State
const showInteractionDialog = ref(false);
const interactionSettings = ref<InteractionSettings>({
  replyOption: 'anyone',
  replyRules: {
    followers: false,
    following: false,
    mentioned: false,
    lists: []
  },
  allowQuotes: true
});

const TAG_CATEGORIES = [
  { id: 'gamedevid', icon: 'mdi-home', label: 'Home/General', description: 'Pos umum pengembangan game. Contoh: Update progress harian.' },
  { id: 'gdiprog', icon: 'mdi-code-tags', label: 'Programming', description: 'Fokus pada kode, skrip, dan logika game. Contoh: Snippet C#, shader, bug-fixing.' },
  { id: 'gdiengine', icon: 'mdi-engine', label: 'Engine', description: 'Diskusi seputar Unity, Unreal, Godot, dll. Contoh: Troubleshooting prefab.' },
  { id: 'gdiart', icon: 'mdi-palette', label: 'Art', description: 'Visual game: 2D, 3D, animasi, UI/UX. Contoh: Model 3D baru, konsep karakter.' },
  { id: 'gdidesign', icon: 'mdi-pencil-ruler', label: 'Design', description: 'Game design, mekanik, narasi, level design. Contoh: Dokumen GDD, puzzle.' },
  { id: 'gdidevlog', icon: 'mdi-notebook-edit', label: 'Devlog', description: 'Catatan proses pengembangan. Contoh: Rangkuman fitur minggu ini.' },
  { id: 'gdirelease', icon: 'mdi-rocket-launch', label: 'Release', description: 'Pengumuman rilis game, demo, atau update. Contoh: Sekarang tersedia di Steam!' },
  { id: 'gdicollab', icon: 'mdi-account-group', label: 'Collab', description: 'Mencari tim atau menawarkan jasa. Contoh: Cari artist untuk game jam.' },
  { id: 'gdihelp', icon: 'mdi-help-circle', label: 'Help', description: 'Tanya jawab atau butuh bantuan teknis. Contoh: Kenapa karakter nembus lantai?' },
  { id: 'gdijob', icon: 'mdi-briefcase', label: 'Jobs', description: 'Lowongan kerja atau portofolio. Contoh: Dibuka posisi Junior Programmer.' },
  { id: 'gdinote', icon: 'mdi-note-text', label: 'Notes', description: 'Catatan Gamedev Note' }
];

const toggleTag = (tagId: string) => {
  const index = selectedTags.value.indexOf(tagId);
  if (index === -1) {
    selectedTags.value.push(tagId);
  } else {
    selectedTags.value.splice(index, 1);
  }
};

const interactionIcon = computed(() => {
  if (interactionSettings.value.replyOption === 'anyone') return 'mdi-earth';
  if (interactionSettings.value.replyOption === 'nobody') return 'mdi-lock';
  return 'mdi-account-check';
});

const interactionLabel = computed(() => {
  const s = interactionSettings.value;
  if (s.replyOption === 'anyone') return 'Everyone';
  if (s.replyOption === 'nobody') return 'Nobody';
  
  const rules = [];
  if (s?.replyRules?.mentioned) rules.push('Mentioned');
  if (s?.replyRules?.following) rules.push('Following');
  if (s?.replyRules?.followers) rules.push('Followers');
  if (s?.replyRules?.lists && s.replyRules.lists.length > 0) rules.push(`${s.replyRules.lists.length} lists`);
  
  return rules.join(', ');
});

const onInteractionSettingsSave = (settings: any) => {
  interactionSettings.value = settings;
};

const rawTextCount = computed(() => text.value.length);
const tagsLength = computed(() => {
  if (selectedTags.value.length === 0) return 0;
  // Sum of tag lengths + 1 space for each tag
  return selectedTags.value.reduce((acc, tag) => acc + tag.length + 2, 0); // +2 for # and space
});
const textCount = computed(() => rawTextCount.value + tagsLength.value);
const isValid = computed(() => {
  const hasContent = text.value.trim().length > 0 || mediaFiles.value.length > 0 || selectedTags.value.length > 0;
  // It's valid if there is content. 
  // If > 300 chars, it will be handled as "Make as Note" instead of a post.
  return hasContent;
});

const instagramLinks = computed(() => {
  return extractInstagramLinks(text.value || '');
});

const handleFiles = async (files: FileList | File[]) => {
  const fileArray = Array.from(files);
  
  for (const file of fileArray) {
    if (mediaFiles.value.length >= 4) break;
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) continue;
    
    // Check if it's a video and if we already have media
    if (file.type.startsWith('video/') && mediaFiles.value.length > 0) {
      await showAlert('You can only upload one video and no other media.');
      continue;
    }
    
    // Check if we already have a video
    if (mediaFiles.value.some(f => f.type.startsWith('video/'))) {
      await showAlert('You can only upload one video.');
      continue;
    }

    mediaFiles.value.push(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      mediaPreviews.value.push({
        url: e.target?.result,
        type: file.type.startsWith('video/') ? 'video' : 'image'
      });
    };
    reader.readAsDataURL(file);
  }
};

const onFileSelected = async (event: any) => {
  const files = event.target.files;
  if (!files) return;
  await handleFiles(files);
  // Clear input value so same file can be selected again
  if (fileInput.value) fileInput.value.value = '';
};

const handlePaste = async (event: ClipboardEvent) => {
  const items = event.clipboardData?.items;
  if (!items) return;
  
  const files: File[] = [];
  for (let i = 0; i < items.length; i++) {
    if (items[i].kind === 'file') {
      const file = items[i].getAsFile();
      if (file) files.push(file);
    }
  }
  
  if (files.length > 0) {
    isExpanded.value = true;
    await handleFiles(files);
  }
};

const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  isDragging.value = true;
};

const handleDragEnter = (event: DragEvent) => {
  event.preventDefault();
  isDragging.value = true;
};

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault();
  isDragging.value = false;
};

const handleDrop = async (event: DragEvent) => {
  event.preventDefault();
  isDragging.value = false;
  isExpanded.value = true;
  
  const files = event.dataTransfer?.files;
  if (files) {
    await handleFiles(files);
  }
};

const removeMedia = (index: number) => {
  mediaFiles.value.splice(index, 1);
  mediaPreviews.value.splice(index, 1);
};

const addEmoji = (emoji: any) => {
  text.value += emoji.native;
};

const handlePost = async () => {
  if (!isValid.value || loading.value) return;

  loading.value = true;
  try {
    let embed = undefined;
    let singleLargeGif = false;

    // Handle Media Uploads
    if (mediaFiles.value.length > 0) {
      // Check if a single large GIF needs to be converted to video
      singleLargeGif = mediaFiles.value.length === 1 && isGifTooLarge(mediaFiles.value[0]);

      if (singleLargeGif) {
        const gifFile = mediaFiles.value[0];
        // Get GIF dimensions for aspectRatio
        const aspectRatio = await new Promise<{ width: number; height: number } | undefined>(resolve => {
          const img = new Image();
          img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
          img.onerror = () => resolve(undefined);
          img.src = URL.createObjectURL(gifFile);
        });
        // Try recommended method: send GIF directly to video.bsky.app (converts to MP4)
        gifStatus.value = { label: 'Uploading GIF...', percent: 5 };
        let videoBlob: any;
        try {
          videoBlob = await uploadVideo(gifFile, (label, percent) => {
            gifStatus.value = { label, percent };
          });
        } catch (e) {
          // Fallback: upload directly to PDS (video.bsky.app will process via firehose)
          console.warn('[CreatePost] uploadVideo failed, falling back to uploadBlob:', e);
          gifStatus.value = { label: 'Uploading...', percent: 90 };
          videoBlob = await uploadBlob(gifFile, 'image/gif');
        }
        gifStatus.value = null;
        embed = {
          $type: 'app.bsky.embed.video',
          video: videoBlob,
          ...(aspectRatio && { aspectRatio }),
          presentation: 'gif',
        };
      } else {
        const blobs = await Promise.all(
          mediaFiles.value.map(async file => {
            if (file.type.startsWith('video/')) {
              return uploadBlob(file, file.type);
            }
            if (file.type === 'image/gif') {
              return uploadBlob(file, 'image/gif');
            }
            const compressed = await compressImage(file);
            return uploadBlob(compressed, 'image/jpeg');
          })
        );

        // Check if there's a video (Bluesky only allows 1 video or up to 4 images)
        const hasVideo = mediaFiles.value.some(f => f.type.startsWith('video'));

        if (hasVideo) {
          embed = { $type: 'app.bsky.embed.video', video: blobs[0] };
        } else {
          embed = {
            $type: 'app.bsky.embed.images',
            images: blobs.map((blob, i) => ({ image: blob, alt: `Image ${i + 1}` }))
          };
        }
      }
    }

    // Append selected tags
    let finalPostText = text.value.trim();
    if (selectedTags.value.length > 0) {
      const tagsString = selectedTags.value.map(t => `#${t}`).join(' ');
      if (finalPostText) {
        finalPostText += '\n\n' + tagsString;
      } else {
        finalPostText = tagsString;
      }
    }

    // Handle Quoted Post (Record Embed)
    if (props.quote) {
      const quoteEmbed = {
        $type: 'app.bsky.embed.record',
        record: {
          uri: props.quote.uri,
          cid: props.quote.cid
        }
      };

      if (embed) {
        // If we have images/video, we need a recordWithMedia embed
        embed = {
          $type: 'app.bsky.embed.recordWithMedia',
          record: quoteEmbed,
          media: embed
        };
      } else {
        embed = quoteEmbed;
      }
    }

    const wasLargeGif = singleLargeGif;
    const response = await createPost(finalPostText, embed, interactionSettings.value);
    
    // Reset form
    text.value = '';
    selectedTags.value = ['gamedevid'];
    mediaFiles.value = [];
    mediaPreviews.value = [];
    interactionSettings.value = {
      replyOption: 'anyone',
      replyRules: {
        followers: false,
        following: false,
        mentioned: false,
        lists: []
      },
      allowQuotes: true
    };
    isExpanded.value = false;
    if (wasLargeGif) {
      showAlert('Your GIF was posted!');
    }
    emit('success', response?.uri);
  } catch (error) {
    console.error('Failed to post:', error);
    await showAlert('Failed to publish post. Please check your connection and try again.');
  } finally {
    loading.value = false;
    gifStatus.value = null;
  }
};
</script>

<style scoped>
.create-post-card {
  transition: all 0.2s ease;
}

.dragging-active {
  border-style: dashed !important;
  background: rgba(var(--v-theme-primary), 0.1) !important;
  transform: scale(1.01);
}

.create-post-textarea {
  font-size: 1.25rem;
  line-height: 1.4;
}

.create-post-textarea :deep(.v-field__input),
.create-post-textarea :deep(textarea) {
  padding-top: 16px !important;
  margin-top: 2px;
}

.bg-black-05 {
  background: rgba(0, 0, 0, 0.2) !important;
}

.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}

.preview-item {
  width: 120px;
  height: 120px;
}

.remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.6) !important;
  color: white !important;
}

.video-badge {
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.6);
  padding: 2px 4px;
  border-radius: 4px;
  color: white;
}

.gap-2 {
  gap: 8px;
}

.tag-activator-btn {
  letter-spacing: 0.1px !important;
  max-width: 100%;
}

.tag-menu-card {
  background: rgba(15, 25, 35, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
}

:deep(.tag-menu-overlay .v-overlay__scrim) {
  backdrop-filter: blur(8px) !important;
  -webkit-backdrop-filter: blur(8px) !important;
  opacity: 1 !important;
  background: rgba(0, 0, 0, 0.4) !important;
}

.tag-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.footnote-text {
  line-height: 1.4;
  word-break: break-all;
  white-space: pre-wrap;
  display: block;
  width: 100%;
}
.tag-badge {
  background: rgba(var(--v-theme-primary), 0.1);
  color: rgb(var(--v-theme-primary));
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 800;
  border: 1px solid rgba(var(--v-theme-primary), 0.2);
}
.ga-2 {
  gap: 8px !important;
}

.min-height-40 {
  min-height: 48px;
}

.animate-fade-in {
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(2px); }
  to { opacity: 1; transform: translateY(0); }
}



</style>
